import {
  ArrowRight,
  Check,
  TrendingUp,
  Shield,
  Zap,
  Users,
  Star,
  BarChart3,
  Wallet,
  Award,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

export default function Services() {
  const [hoveredService, setHoveredService] = useState(null);

  // UPDATED: services reflect managed-trading + profit-sharing model
  const services = [
    {
      t: "Managed Trading Accounts",
      d: "We trade your capital using disciplined, rule-based strategies so you don’t have to watch charts all day.",
      icon: TrendingUp,
      features: [
        "Hands-free for you",
        "Defined rules for entries & exits",
        "Daily risk limits in place",
      ],
    },
    {
      t: "Profit-Sharing Partnership",
      d: "Simple model: you invest, we trade, and profits are shared based on a pre-agreed percentage.",
      icon: Wallet,
      features: [
        "No profit, no performance fee",
        "Aligned incentives",
        "Clear written terms",
      ],
    },
    {
      t: "Multi-Market Strategies",
      d: "We focus on a selected basket of markets to diversify risk and capture opportunities.",
      icon: BarChart3,
      features: [
        "Forex & index-focused setups",
        "Multiple timeframes",
        "Back-tested systems",
      ],
    },
    {
      t: "Risk Management & Allocation",
      d: "Capital protection comes first – every trade and position is controlled by predefined risk rules.",
      icon: Shield,
      features: [
        "Max drawdown thresholds",
        "Position sizing rules",
        "Capital-first mindset",
      ],
    },
    {
      t: "Reporting & Communication",
      d: "Stay updated with clear performance summaries and direct access to our team.",
      icon: Users,
      features: [
        "Periodic performance reports",
        "Plain-language explanations",
        "Direct call/WhatsApp support",
      ],
    },
    {
      t: "Education & Orientation",
      d: "Before you start, we explain risk, process, and expectations so you can take an informed decision.",
      icon: Award,
      features: [
        "Pre-onboarding call",
        "Risk awareness session",
        "No ‘guaranteed returns’ promises",
      ],
    },
  ];

  // UPDATED: stats reflect partnership / experience, not broker metrics
  const stats = [
    { value: "7+ Years", label: "Trading Experience" },
    { value: "300+", label: "Active Partners" },
    { value: "3–12 mo", label: "Typical Horizon" },
    { value: "5+", label: "Core Strategies Used" },
  ];

  return (
    <>
      {/* HERO SECTION - Updated copy for investment + profit-sharing */}
      <section className="relative bg-gradient-to-br from-slate-900 via-black to-slate-900 pt-32 pb-24 overflow-hidden">
        {/* Animated background grid */}
        <div className="absolute inset-0 bg-grid-white/5"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="inline-block mb-6 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-semibold animate-fade-in">
            Invest with Professionals • Share the Profits
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-fade-in">
            Let Professionals Trade
            <br />
            <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">
              While You Focus on Life
            </span>
          </h1>

          <p
            className="text-xl md:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto animate-fade-in"
            style={{ animationDelay: "0.2s" }}
          >
            You invest. Our team trades with disciplined strategies and risk
            controls. Profits are shared through a transparent partnership
            model.
          </p>

          <div
            className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in"
            style={{ animationDelay: "0.4s" }}
          >
            <Link
              to="/contact"
              className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold rounded-xl transition-all transform hover:scale-105 flex items-center justify-center gap-2 group shadow-lg shadow-cyan-500/50"
            >
              Talk About Investment
              <ArrowRight className="group-hover:translate-x-1 transition" />
            </Link>
          </div>

          {/* Stats bar */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {stats.map((stat, i) => (
              <div
                key={i}
                className="text-center animate-fade-in"
                style={{ animationDelay: `${0.6 + i * 0.1}s` }}
              >
                <div className="text-3xl md:text-4xl font-bold text-cyan-400 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-500 mt-6">
            *Trading involves risk of loss. Past performance does not guarantee
            future results. We focus on risk management, not guaranteed returns.
          </p>
        </div>
      </section>

      {/* SERVICES - Updated descriptions but same UI */}
      <section id="services" className="py-20 bg-black">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Core Services
              </span>
            </h2>
            <p className="text-gray-400 text-lg">
              Structured offerings built around managed trading & profit sharing
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((item, i) => (
              <div
                key={i}
                onMouseEnter={() => setHoveredService(i)}
                onMouseLeave={() => setHoveredService(null)}
                className="group relative bg-gradient-to-br from-white/5 to-white/10 border border-white/20 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300 overflow-hidden cursor-pointer transform hover:-translate-y-2"
              >
                {/* Hover glow effect */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br from-cyan-500/0 to-blue-500/0 group-hover:from-cyan-500/10 group-hover:to-blue-500/10 transition-all duration-500 rounded-2xl`}
                ></div>

                <div className="relative">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center group-hover:bg-cyan-500/30 transition-all">
                      <item.icon className="w-6 h-6 text-cyan-400" />
                    </div>
                    <span className="text-3xl font-bold text-white/10 group-hover:text-white/20 transition-all">
                      0{i + 1}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                    {item.t}
                  </h3>
                  <p className="text-gray-300 mb-4">{item.d}</p>

                  {/* Features list - shows on hover */}
                  <div
                    className={`space-y-2 mb-4 transition-all duration-300 ${
                      hoveredService === i
                        ? "opacity-100 max-h-32"
                        : "opacity-0 max-h-0 overflow-hidden"
                    }`}
                  >
                    {item.features.map((feature, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 text-sm text-cyan-400"
                      >
                        <Check className="w-4 h-4" />
                        {feature}
                      </div>
                    ))}
                  </div>

                  <button className="text-cyan-400 hover:text-cyan-300 font-medium flex items-center gap-2 group/btn">
                    Learn more
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-2 transition" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US - Updated reasons for partnership */}
      <section className="py-20 bg-gradient-to-b from-slate-900 to-black">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Why Partners{" "}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Choose Us
            </span>
          </h2>
          <p className="text-center text-gray-400 text-lg mb-12">
            Not just trades — a structured, disciplined partnership around your
            capital
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Zap,
                title: "Disciplined Execution",
                desc: "Rule-based entries, exits, and risk — not emotional decisions.",
                color: "from-yellow-400 to-orange-500",
              },
              {
                icon: Shield,
                title: "Risk-First Thinking",
                desc: "Capital protection and drawdown control come before profit targets.",
                color: "from-green-400 to-emerald-500",
              },
              {
                icon: TrendingUp,
                title: "Aligned Incentives",
                desc: "Profit-sharing model — we grow only when you grow.",
                color: "from-cyan-400 to-blue-500",
              },
              {
                icon: Users,
                title: "Human Support",
                desc: "Real conversations, honest guidance, and clear expectations.",
                color: "from-purple-400 to-pink-500",
              },
            ].map((item, i) => (
              <div key={i} className="text-center group cursor-pointer">
                <div
                  className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-br ${item.color} opacity-20 rounded-2xl flex items-center justify-center group-hover:opacity-30 transition-all transform group-hover:scale-110 group-hover:rotate-3`}
                >
                  <item.icon
                    className={`w-9 h-9 bg-gradient-to-br ${item.color} bg-clip-text text-transparent`}
                    style={{
                      WebkitTextFillColor: "transparent",
                      WebkitBackgroundClip: "text",
                    }}
                  />
                </div>
                <h3 className="text-xl font-bold mb-2 group-hover:text-cyan-400 transition-colors">
                  {item.title}
                </h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA - Updated message & removed bonus/marketing gimmicks */}
      <section className="relative py-20 bg-gradient-to-r from-cyan-600 via-blue-600 to-purple-600 overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Explore a Trading Partnership?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Share your goals and capital range with us. We&apos;ll walk you
            through the risk, the process, and how profit sharing can work for
            you — with no obligation to start.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-10 py-5 border-2 border-white text-white font-bold rounded-xl hover:bg-white/10 transition-all text-lg transform hover:scale-105"
            >
              Contact Us
            </Link>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-white/90">
            <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
              <Check className="w-5 h-5" /> No hidden fees in our model
            </span>
            <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
              <Check className="w-5 h-5" /> Clear explanation of risks
            </span>
            <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
              <Check className="w-5 h-5" /> Human-to-human conversation
            </span>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
        
        .bg-grid-white\\/5 {
          background-image: 
            linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
          background-size: 50px 50px;
        }
      `}</style>
    </>
  );
}
